import { z } from 'zod';
import { widgetLayoutSchema } from '@/config/schema';
import { fontOptionsSchema } from '@/lib/fontStyle';

/**
 * The schemas the departure board and its three child components share.
 *
 * Separate from the `.tsx` files because four modules need them and a file exporting both a
 * component and a schema cannot be fast-refreshed.
 */

/**
 * `options.component` — one font block per level of the board, and the only thing the three
 * child components take besides their data. Every level defaults to 1.2rem
 * (docs/MIGRATION-WIDGETS.md §2); `szeged_2`/`3`/`4` set 2 / 1.4 / 1.2 / 1.2.
 */
export const departureFontsSchema = z.object({
  /** `departureStation.vue` — the stop name. */
  station: fontOptionsSchema.optional(),
  /** `departureLine.vue` — the boxed line number. */
  line: fontOptionsSchema.optional(),
  /** `departureTimes.vue` — the direction the line is heading. */
  heading: fontOptionsSchema.optional(),
  /** `departureTimes.vue` — the list of departure times under a heading. */
  times: fontOptionsSchema.optional(),
});

export type DepartureFonts = z.infer<typeof departureFontsSchema>;

/** Every level of the board falls back to this. */
export const DEPARTURE_FONT_SIZE = '1.2rem';

/**
 * Tailwind 2's default border colour, stated explicitly.
 *
 * This is the only widget in the app with a border (`border-1` on the line box, `border-b-1`
 * under each heading), and the default the two utilities inherit **changed between Tailwind
 * versions**: v2's preflight sets `border-color: rgba(229, 231, 235, …)` — `gray-200` — on
 * every element, while v4 reverted to `currentColor`. Leaving it unset would therefore paint
 * these borders in the configured *text* colour (white on all three szeged displays) rather
 * than the pale grey that is on the wall today.
 *
 * Not fixed globally in `index.css`, deliberately: a preflight override is a site-wide theming
 * decision (SYM-502's territory) and nothing else in the app draws a border, so the narrower
 * change is the one that can be reviewed by looking at the element it affects.
 */
export const LEGACY_BORDER_COLOR = 'rgb(229, 231, 235)';

export const departureOptionsSchema = z.object({
  topic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  title: z.string().optional(),
  titleFont: fontOptionsSchema.optional(),
  textBloc: z.object({ text: z.string().default('') }).prefault({}),
  textBlocFont: fontOptionsSchema.optional(),
  component: departureFontsSchema.prefault({}),
});

/**
 * The **transform's output**, not the broker payload — `departureTransform` (SYM-490) has
 * already turned `{ station: { line: { heading: [ISO datetime] } } }` into this and formatted
 * the times as `HH:mm`. docs/MIGRATION-WIDGETS.md §7 documents the payload it consumes.
 *
 * Validated again at the widget boundary because a widget rendered outside `WidgetSlot`, or a
 * config whose `type` changed after the topic was claimed, can put an unshaped payload in the
 * store — and the difference between "no stations" and a thrown `undefined.transports` on a
 * wall is this schema.
 */
export const departureBoardSchema = z.array(
  z.object({
    name: z.string(),
    transports: z.array(
      z.object({
        name: z.string(),
        headings: z.array(z.object({ heading: z.string(), times: z.array(z.string()) })),
      }),
    ),
  }),
);
